/**
 * Pure H OS · Pre-Production Gap Closure · Executive Inbox.
 * A single triage surface: everything that wants the owner's attention,
 * across Notifications, Approvals, AI Insights (open), and Decisions.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { ListRow, TabBar } from "../components/lib.overlay";
import { AI_INSIGHTS, AI_APPROVALS, AI_TEAMMATES } from "../mocks/aiData";
import { PROCUREMENT } from "../mocks/procurementData";
import { DECISIONS }   from "../mocks/decisionsData";
import { RISKS_REGISTRY } from "../mocks/risksRegistryData";
import { MEETINGS }    from "../mocks/meetingsData";
import { readCollection } from "../services/store";
import { useLiveTick } from "../lib/useLiveTick";

type Row = {
  key: string;
  kind: "AI Rec" | "AI Approval" | "PO" | "Decision" | "Risk" | "Meeting";
  title: string;
  detail: string;
  severity: "low" | "medium" | "high";
  href: string;
};
type Tab = "all" | "attention" | "AI" | "governance";
type RecState = { id: string; state: "open" | "acted" | "dismissed" | "converted" };

function tname(id: string) { return AI_TEAMMATES.find((t) => t.id === id)?.name ?? id; }

export default function ExecutiveInbox() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("attention");
  useLiveTick();

  const rows: Row[] = useMemo(() => {
    const states = readCollection<RecState>("rec_states");
    const stateOf = (id: string) => states.find((s) => s.id === id)?.state ?? "open";

    const recs: Row[] = AI_INSIGHTS
      .filter((i) => stateOf(i.id) === "open")
      .map((i) => ({
        key: `rec-${i.id}`,
        kind: "AI Rec",
        title: i.title,
        detail: `${tname(i.teammateId)} · ${i.createdRelative} — ${i.detail}`,
        severity: i.severity === "high" ? "high" : i.severity === "medium" ? "medium" : "low",
        href: "/pureh/ai/insights",
      }));

    const aiA: Row[] = AI_APPROVALS
      .filter((a) => a.status === "pending")
      .map((a) => ({
        key: `aia-${a.id}`,
        kind: "AI Approval",
        title: a.title,
        detail: `Requested by ${a.askedBy} · ${a.askedRelative}`,
        severity: "medium" as const,
        href: "/pureh/approvals",
      }));

    const pos: Row[] = PROCUREMENT
      .filter((p) => p.status === "pending" || p.status === "draft")
      .map((p) => ({
        key: `po-${p.id}`,
        kind: "PO",
        title: `${p.poNumber} · ${p.supplier}`,
        detail: `${p.category} · EGP ${p.amountEGP.toLocaleString()} · ordered ${p.orderedRelative}`,
        severity: p.status === "pending" ? "medium" : "low",
        href: `/pureh/procurement?open=${p.id}`,
      }));

    const decs: Row[] = DECISIONS
      .filter((d) => d.outcome === "hold" || d.outcome === "deferred")
      .map((d) => ({
        key: `dec-${d.id}`,
        kind: "Decision",
        title: d.title,
        detail: `${d.module} · ${d.decidedBy} · ${d.dateRelative}`,
        severity: d.severity,
        href: `/pureh/decisions?open=${d.id}`,
      }));

    const openRisks: Row[] = RISKS_REGISTRY
      .filter((r) => r.status === "open" && r.severity === "high")
      .map((r) => ({
        key: `risk-${r.id}`,
        kind: "Risk",
        title: r.title,
        detail: `${r.module} · ${r.owner} · updated ${r.updatedRelative}`,
        severity: r.severity,
        href: `/pureh/risks?open=${r.id}`,
      }));

    const todayMeetings: Row[] = MEETINGS
      .filter((m) => /today/i.test(m.scheduledRelative) && m.status === "scheduled")
      .map((m) => ({
        key: `mtg-${m.id}`,
        kind: "Meeting",
        title: m.title,
        detail: `${m.scheduledRelative} · ${m.attendees.length} attendees · ${m.owner}`,
        severity: "medium",
        href: `/pureh/meetings?open=${m.id}`,
      }));

    return [...recs, ...aiA, ...pos, ...decs, ...openRisks, ...todayMeetings];
  }, []);

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    if (tab === "attention") return rows.filter((r) => r.severity === "high" || r.kind === "AI Approval" || r.kind === "Meeting");
    if (tab === "AI") return rows.filter((r) => r.kind === "AI Rec" || r.kind === "AI Approval");
    return rows.filter((r) => r.kind === "PO" || r.kind === "Decision" || r.kind === "Risk");
  }, [rows, tab]);

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <Card testId="inbox-filters">
        <TabBar<Tab>
          testId="inbox-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "attention",  label: `Needs attention ${rows.filter((r) => r.severity === "high" || r.kind === "AI Approval" || r.kind === "Meeting").length}` },
            { key: "AI",         label: `AI ${rows.filter((r) => r.kind === "AI Rec" || r.kind === "AI Approval").length}` },
            { key: "governance", label: `Governance ${rows.filter((r) => r.kind === "PO" || r.kind === "Decision" || r.kind === "Risk").length}` },
            { key: "all",        label: `All ${rows.length}` },
          ]}
        />
      </Card>

      <Card padded={false} testId="inbox-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Executive Inbox" title={`${filtered.length} item${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>
            One triage queue — AI recommendations, approvals, pending POs, deferred decisions, high-severity open risks, meetings today.
          </div>
        </div>
        <div className="p-2">
          {filtered.length === 0 ? (
            <div className="p-5 text-sm" style={{ color: "var(--ph-muted)" }}>Inbox zero.</div>
          ) : filtered.map((r) => (
            <ListRow key={r.key} testId={`inbox-${r.key}`}
              onClick={() => navigate(r.href)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{r.kind}</span>
                  <span>{r.title}</span>
                </span>
              }
              sublabel={<>{r.detail}</>}
              trailing={<SeverityBadge severity={r.severity} />}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}
